import { marked } from 'marked';
import DOMPurify from 'dompurify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const API_KEY = process.env.REACT_APP_API_KEY;

class HybridBlogService {
  constructor() {
    this.setupMarked();
    this.cache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
  }

  setupMarked() {
    marked.setOptions({
      breaks: true,
      gfm: true,
      headerIds: true,
      mangle: false,
      pedantic: false,
      sanitize: false,
      smartLists: true,
      smartypants: true,
    });
  }

  isValidCache(key) {
    const cached = this.cache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < this.cacheExpiry;
  }

  getFromCache(key) {
    return this.cache.get(key)?.data;
  }

  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  async fetchFromAPI(endpoint) {
    const response = await fetch(`${API_BASE_URL}/api/v1/${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(API_KEY && { 'X-API-Key': API_KEY }),
      }
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return await response.json();
  }

  async fetchGitHubContent(githubUrl) {
    if (!githubUrl) {
      throw new Error('GitHub URL not provided');
    }

    const cacheKey = `github-${githubUrl}`;
    if (this.isValidCache(cacheKey)) {
      return this.getFromCache(cacheKey);
    }

    const response = await fetch(githubUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch GitHub content: ${response.statusText}`);
    }

    const content = await response.text();
    this.setCache(cacheKey, content);
    return content;
  }

  transformBlogMetadata(blog) {
    return {
      id: blog.id,
      slug: blog.slug,
      title: blog.title || 'Untitled',
      excerpt: blog.excerpt || '',
      image: blog.featured_image_url || '/assets/images/blog/blog-placeholder.webp',
      author: blog.author || 'Sun Catchers Team',
      category: blog.category || 'Travel Guide',
      date: blog.published_at,
      tags: blog.tags || [],
      featured: blog.featured || false,
      readTime: `${blog.read_time_minutes || 5} Min Read`,
      viewCount: blog.view_count || 0,
      githubUrl: blog.github_url,
      githubSlug: blog.github_slug
    };
  }

  async fetchBlogList() {
    const cacheKey = 'blog-list';
    if (this.isValidCache(cacheKey)) {
      return this.getFromCache(cacheKey);
    }

    try {
      const response = await this.fetchFromAPI('blogs');
      const blogs = response.blogs.map(blog => this.transformBlogMetadata(blog));

      this.setCache(cacheKey, blogs);
      return blogs;
    } catch (error) {
      console.error('Error fetching blog list from API, falling back to GitHub-only mode:', error);

      // Return cached data if available, even if expired
      const cachedData = this.getFromCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      // Fallback to GitHub-only mode
      return this.fetchBlogListFromGitHub();
    }
  }

  async fetchBlogListFromGitHub() {
    try {
      const response = await fetch(
        `https://api.github.com/repos/worldwidedata/suncatchers-blogs/contents?ref=main`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch blog list from GitHub');
      }

      const files = await response.json();

      const markdownFiles = files.filter(file =>
        file.type === 'file' && file.name.endsWith('.md')
      );

      const blogs = await Promise.all(
        markdownFiles.map(async (file, index) => {
          try {
            const metadata = await this.fetchGitHubFileMetadata(file.name, file.download_url);
            return {
              id: index + 1,
              slug: file.name.replace('.md', ''),
              title: metadata.title || file.name.replace('.md', '').replace(/-/g, ' '),
              excerpt: metadata.excerpt || 'Read this blog post...',
              image: metadata.image || '/assets/images/blog/blog-placeholder.webp',
              author: metadata.author || 'Sun Catchers Team',
              category: metadata.category || 'Travel Guide',
              date: metadata.date || new Date().toISOString(),
              tags: metadata.tags || [],
              featured: false,
              readTime: '5 Min Read',
              viewCount: 0,
              githubUrl: file.download_url,
              githubSlug: file.name.replace('.md', '')
            };
          } catch (err) {
            console.warn(`Failed to fetch metadata for ${file.name}:`, err);
            return {
              id: index + 1,
              slug: file.name.replace('.md', ''),
              title: file.name.replace('.md', '').replace(/-/g, ' '),
              excerpt: 'Read this blog post...',
              image: '/assets/images/blog/blog-placeholder.webp',
              author: 'Sun Catchers Team',
              category: 'Travel Guide',
              date: new Date().toISOString(),
              tags: [],
              featured: false,
              readTime: '5 Min Read',
              viewCount: 0,
              githubUrl: file.download_url,
              githubSlug: file.name.replace('.md', '')
            };
          }
        })
      );

      return blogs.sort((a, b) => new Date(b.date) - new Date(a.date));
    } catch (error) {
      console.error('GitHub fallback also failed:', error);
      return []; // Return empty array as final fallback
    }
  }

  async fetchGitHubFileMetadata(filename, downloadUrl) {
    try {
      const rawContent = await this.fetchGitHubContent(downloadUrl);
      return this.parseMarkdownMetadata(rawContent, filename);
    } catch (error) {
      console.warn('Failed to fetch GitHub file metadata:', error);
      return {};
    }
  }

  parseMarkdownMetadata(rawContent, filename) {
    const metadata = {
      title: '',
      date: '',
      author: '',
      excerpt: '',
      tags: [],
      image: '',
      category: ''
    };

    // Check for YAML frontmatter
    if (rawContent.startsWith('---')) {
      const frontMatterEnd = rawContent.indexOf('---', 3);
      if (frontMatterEnd > -1) {
        const frontMatter = rawContent.substring(3, frontMatterEnd).trim();
        const content = rawContent.substring(frontMatterEnd + 3).trim();

        // Parse YAML frontmatter
        frontMatter.split('\n').forEach(line => {
          const colonIndex = line.indexOf(':');
          if (colonIndex > -1) {
            const key = line.substring(0, colonIndex).trim();
            let value = line.substring(colonIndex + 1).trim();

            // Remove quotes if present
            value = value.replace(/^["']|["']$/g, '');

            if (key === 'tags') {
              // Handle array format: ["tag1", "tag2"] or [tag1, tag2]
              metadata.tags = value
                .replace(/[[\]]/g, '')
                .split(',')
                .map(tag => tag.trim().replace(/["']/g, ''))
                .filter(tag => tag.length > 0);
            } else {
              metadata[key] = value;
            }
          }
        });

        // Generate excerpt from content if not provided
        if (!metadata.excerpt && content) {
          const plainText = content.replace(/[#*`]/g, '').trim();
          metadata.excerpt = plainText.substring(0, 200) + (plainText.length > 200 ? '...' : '');
        }
      }
    }

    // Fallback: extract title from first heading if not in frontmatter
    if (!metadata.title) {
      const titleMatch = rawContent.match(/^#\s+(.+)$/m);
      if (titleMatch) {
        metadata.title = titleMatch[1];
      } else {
        metadata.title = filename.replace('.md', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
    }

    // Generate excerpt from content if still not available
    if (!metadata.excerpt) {
      const contentWithoutTitle = rawContent.replace(/^#\s+.+$/m, '').trim();
      const plainText = contentWithoutTitle.replace(/[#*`]/g, '').trim();
      metadata.excerpt = plainText.substring(0, 200) + (plainText.length > 200 ? '...' : '');
    }

    return metadata;
  }

  async fetchBlogBySlug(slug) {
    const cacheKey = `blog-${slug}`;
    if (this.isValidCache(cacheKey)) {
      return this.getFromCache(cacheKey);
    }

    try {
      const response = await this.fetchFromAPI(`blogs/${slug}`);
      const blogMetadata = this.transformBlogMetadata(response.blog);

      // If there's a GitHub URL, fetch the markdown content
      let markdownContent = '';
      if (blogMetadata.githubUrl) {
        try {
          markdownContent = await this.fetchGitHubContent(blogMetadata.githubUrl);
        } catch (githubError) {
          console.warn('Failed to fetch GitHub content, using database content:', githubError);
          markdownContent = response.blog.content || '';
        }
      } else {
        // Fall back to database content
        markdownContent = response.blog.content || '';
      }

      // Parse markdown content
      let parsedContent = '';
      if (markdownContent) {
        // Remove YAML frontmatter if present (since we have metadata from database)
        let contentToParse = markdownContent;
        if (markdownContent.startsWith('---')) {
          const frontMatterEnd = markdownContent.indexOf('---', 3);
          if (frontMatterEnd > -1) {
            contentToParse = markdownContent.substring(frontMatterEnd + 3).trim();
          }
        }

        const htmlContent = marked.parse(contentToParse);
        parsedContent = DOMPurify.sanitize(htmlContent, {
          ADD_ATTR: ['target', 'rel'],
          ADD_TAGS: ['iframe'],
          ALLOW_DATA_ATTR: false
        });
      }

      const result = {
        ...blogMetadata,
        content: parsedContent,
        rawContent: markdownContent
      };

      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error fetching blog from API, falling back to GitHub-only mode:', error);
      return this.fetchBlogBySlugFromGitHub(slug);
    }
  }

  async fetchBlogBySlugFromGitHub(slug) {
    try {
      const filename = `${slug}.md`;
      const githubUrl = `https://raw.githubusercontent.com/worldwidedata/suncatchers-blogs/main/${filename}`;

      const rawContent = await this.fetchGitHubContent(githubUrl);
      const metadata = this.parseMarkdownMetadata(rawContent, filename);

      // Parse markdown content
      let content = rawContent;
      let parsedContent = '';

      // Parse frontmatter if present
      if (rawContent.startsWith('---')) {
        const frontMatterEnd = rawContent.indexOf('---', 3);
        if (frontMatterEnd > -1) {
          content = rawContent.substring(frontMatterEnd + 3).trim();
        }
      }

      // Convert markdown to HTML
      const htmlContent = marked.parse(content);
      parsedContent = DOMPurify.sanitize(htmlContent, {
        ADD_ATTR: ['target', 'rel'],
        ADD_TAGS: ['iframe'],
        ALLOW_DATA_ATTR: false
      });

      return {
        id: slug,
        slug: slug,
        title: metadata.title || slug.replace(/-/g, ' '),
        excerpt: metadata.excerpt || 'Read this blog post...',
        image: metadata.image || '/assets/images/blog/blog-placeholder.webp',
        author: metadata.author || 'Sun Catchers Team',
        category: metadata.category || 'Travel Guide',
        date: metadata.date || new Date().toISOString(),
        tags: metadata.tags || [],
        featured: false,
        readTime: '5 Min Read',
        viewCount: 0,
        githubUrl: githubUrl,
        githubSlug: slug,
        content: parsedContent,
        rawContent: rawContent
      };
    } catch (error) {
      console.error('GitHub fallback also failed for blog:', error);
      throw error;
    }
  }

  async fetchFeaturedBlogs(limit = 3) {
    const cacheKey = `featured-blogs-${limit}`;
    if (this.isValidCache(cacheKey)) {
      return this.getFromCache(cacheKey);
    }

    try {
      const response = await this.fetchFromAPI(`blogs/featured?limit=${limit}`);
      const blogs = response.blogs.map(blog => this.transformBlogMetadata(blog));

      this.setCache(cacheKey, blogs);
      return blogs;
    } catch (error) {
      console.error('Error fetching featured blogs:', error);
      throw error;
    }
  }

  async fetchBlogsByCategory(category, limit = 10) {
    const cacheKey = `category-blogs-${category}-${limit}`;
    if (this.isValidCache(cacheKey)) {
      return this.getFromCache(cacheKey);
    }

    try {
      const response = await this.fetchFromAPI(`blogs/category/${encodeURIComponent(category)}?limit=${limit}`);
      const blogs = response.blogs.map(blog => this.transformBlogMetadata(blog));

      this.setCache(cacheKey, blogs);
      return blogs;
    } catch (error) {
      console.error('Error fetching blogs by category:', error);
      throw error;
    }
  }

  async fetchRelatedBlogs(blogSlug, category, tags = [], limit = 3) {
    const cacheKey = `related-blogs-${blogSlug}-${limit}`;
    if (this.isValidCache(cacheKey)) {
      return this.getFromCache(cacheKey);
    }

    try {
      const queryParams = new URLSearchParams({
        limit: limit.toString()
      });

      if (category) {
        queryParams.append('category', category);
      }

      tags.forEach(tag => {
        queryParams.append('tags', tag);
      });

      const response = await this.fetchFromAPI(`blog/${blogSlug}/related?${queryParams.toString()}`);
      const blogs = response.blogs.map(blog => this.transformBlogMetadata(blog));

      this.setCache(cacheKey, blogs);
      return blogs;
    } catch (error) {
      console.error('Error fetching related blogs:', error);
      throw error;
    }
  }

  async searchBlogs(query) {
    if (!query || query.trim() === '') {
      return this.fetchBlogList();
    }

    const cacheKey = `search-${query}`;
    if (this.isValidCache(cacheKey)) {
      return this.getFromCache(cacheKey);
    }

    try {
      const response = await this.fetchFromAPI(`blogs/search?q=${encodeURIComponent(query)}`);
      const blogs = response.blogs.map(blog => this.transformBlogMetadata(blog));

      this.setCache(cacheKey, blogs);
      return blogs;
    } catch (error) {
      console.error('Error searching blogs:', error);
      throw error;
    }
  }

  formatDate(dateString) {
    if (!dateString) return 'Recent';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  clearCache() {
    this.cache.clear();
  }
}

const hybridBlogService = new HybridBlogService();
export default hybridBlogService;